import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash-es';
import classnames from 'classnames';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';

import { connect } from 'react-redux';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';

import { pageDefinition } from 'site-modules/shared/pages/page';
import { setCriticalCss } from 'client/actions/critical-css';
import { getModelState } from 'client/data/luckdragon/model';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { VehicleModel } from 'client/data/models/vehicle';
import { bindToContent, CmsEntities, CmsModel } from 'client/data/models/cms';
import { CpoModel, getCpoContentPath, getCpoPath } from 'client/data/models/cpo';
import { DEFAULT_CONTENT, parseContent } from 'client/data/cms/content';
import { getMeta } from 'site-modules/shared/utils/seo-helper';
import { scrollToHash } from 'client/utils/scroll';
import { CPO_NEWS_WIDGET_CONTENT_PATH } from 'site-modules/shared/constants/editorial/editorial';
import { CPO_CARS_PAGE_CATEGORY, CPO_CARS_PAGE_NAME } from 'site-modules/cpo/constants/cpo';
import { InventoryModel } from 'client/data/models/inventory';

import { TrackingMap } from 'client/tracking/maps/cpo/cpo';
import { TrackingMap as TrackingSRPMap } from 'client/tracking/maps/inventory/srp/index';
import { TrackingHandler } from 'client/tracking/handler';

import { getQuery } from 'client/utils/location';
import { getFilterFromLocation } from 'site-modules/shared/utils/inventory/search-filter';

import { CpoProgramOverview } from 'site-modules/cpo/components/cpo-program-overview/cpo-program-overview';
import { FeaturedSpotlightCarousel } from 'site-modules/shared/components/native-ad/featured-spotlight/featured-spotlight-carousel';
import { ShopCpoInventory } from 'site-modules/cpo/components/shop-cpo-inventory/shop-cpo-inventory';
import { CarsForSale } from 'site-modules/cpo/components/cars-for-sale/cars-for-sale';
import { CpoEditorialArticles } from 'site-modules/cpo/components/cpo-editorial-articles/cpo-editorial-articles';
import { BrowseCpoByType } from 'site-modules/cpo/components/browse-cpo-by-type/browse-cpo-by-type';
import { CpoAd } from 'site-modules/cpo/components/cpo-ad/cpo-ad';
import { ClippedContainer } from 'site-modules/shared/components/clipped-container/clipped-container';
import { NewsWidget } from 'site-modules/cpo/components/news-widget/news-widget';
import { CpoSrp, CPO_SRP_INITIAL_FILTER } from 'site-modules/cpo/components/cpo-srp/cpo-srp';
import convexMask from 'site-modules/shared/components/clipped-container/mask-library/usurp-header';

import './index.scss';

export function CpoLandingPageUI({
  featuredSpotlightAdSlides = null,
  cpoAdContent = DEFAULT_CONTENT,
  location = {},
  isMobile = false,
}) {
  useEffect(() => {
    TrackingHandler.useMap({ ...TrackingMap, ...TrackingSRPMap });
    scrollToHash(get(location, 'hash', ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main id="main-content" tabIndex={-1} className="certified-cars">
      <ClippedContainer containerClasses="curve-header pos-a top-0 left-0 w-100" mask={convexMask} />
      <Container>
        <h1 className="heading-1 fw-bold text-white text-shadow mt-1_75 mb-0_5">
          Shop Certified Pre Owned Cars For Sale
        </h1>
        <Row>
          <Col xs={12} lg={8}>
            <ShopCpoInventory location={location} isMobile={isMobile} />
          </Col>
          <Col xs={12} lg={4} className="d-flex justify-content-md-center justify-content-lg-end">
            <FeaturedSpotlightCarousel
              slides={featuredSpotlightAdSlides}
              className={classnames({ 'mt-md-1 mt-lg-2 pt-0_25': !isMobile, 'mt-3': isMobile })}
            />
          </Col>
        </Row>
        <BrowseCpoByType />
      </Container>
      <div className="bg-blue-100 py-2 mb-3_5">
        <Container>
          <NewsWidget />
        </Container>
      </div>
      <Container className="pt-1">
        <CpoAd entry={cpoAdContent} isMobile={isMobile} className="mb-3_5" />
        <CpoProgramOverview isMobile={isMobile} />
      </Container>
      <CpoSrp isMobile={isMobile} />
      <Container>
        <CpoEditorialArticles isMobile={isMobile} />
        <CarsForSale />
      </Container>
    </main>
  );
}

CpoLandingPageUI.propTypes = {
  featuredSpotlightAdSlides: PropTypes.arrayOf(PropTypes.shape({})),
  cpoAdContent: CmsEntities.Content,
  location: PropTypes.shape({}),
  isMobile: PropTypes.bool,
};

export const CpoLandingPage = connect(state => ({ isMobile: state.mobile }))(
  connectToModel(CpoLandingPageUI, {
    featuredSpotlightAdSlides: bindToPath('featuredSpotlightAd', CpoModel),
    cpoAdContent: bindToContent(getCpoPath(), 'article-offers-ad'),
  })
);

export const pageDefinitionConfiguration = {
  name: CPO_CARS_PAGE_NAME,
  category: CPO_CARS_PAGE_CATEGORY,
  seo: state => {
    const modelState = getModelState(state);

    const cpoLandingContent = modelState.get(getCpoContentPath(), CmsModel);
    return { ...getMeta(parseContent(cpoLandingContent)) };
  },
  preload: (store, props) => {
    const preloader = new ModelPreloader(store);

    preloader.resolve('makesList', VehicleModel);
    preloader.resolve(getCpoContentPath(), CmsModel);
    preloader.resolve(`content["${CPO_NEWS_WIDGET_CONTENT_PATH}"]`, CmsModel);
    preloader.resolve(`inventoryLogo`, CpoModel);
    preloader.resolve(`featuredSpotlightAd`, CpoModel);
    preloader.resolve(`carsForSale`, CpoModel);

    return preloader.load().then(modelState => {
      const make = parseContent(modelState.get(getCpoContentPath(), CmsModel))
        .child('cpo-srp-module')
        .metadata('make')
        .value();

      preloader.set('searchResultsFilter', InventoryModel, {
        make: [make],
        ...CPO_SRP_INITIAL_FILTER,
        ...getFilterFromLocation(getQuery(props.location)),
        pageSize: 2,
        fetchSuggestedFacets: true,
      });
      preloader.resolve('searchResults', InventoryModel);

      const featuredSpotlightAdSlides = modelState.get('featuredSpotlightAd', CpoModel);

      store.dispatch(setCriticalCss({ featuredSpotlightHidden: isEmpty(featuredSpotlightAdSlides) }));

      return preloader.load();
    });
  },
  component: CpoLandingPage,
};

export default pageDefinition(pageDefinitionConfiguration);
